/** Persisted artifact data; independent of UI and locale state. */
export interface ArtifactSource {
  id: string;
  type: 'document' | 'mermaid' | 'todo' | 'html';
  title: string;
  subtitle?: string;
  content: string;
  todoItems?: { id: string; label: string; done: boolean }[];
}

export function extractArtifactFromMarkdown(
  content: string,
  translate: (key: string) => string = (key) => key,
  makeId: (type: string) => string = (type) => `art-${type}`,
): ArtifactSource | null {
  if (!content || typeof content !== 'string') return null;

  // 1. Check for ```mermaid block
  const mermaidMatch = content.match(/```mermaid\s*\n([\s\S]+?)\n```/i);
  if (mermaidMatch && mermaidMatch[1]?.trim()) {
    return {
      id: makeId('mermaid'),
      type: 'mermaid',
      title: translate('crm.artifacts.diagram_title'),
      subtitle: 'Mermaid flowchart / sequence',
      content: mermaidMatch[1].trim(),
    };
  }

  // 2. Check for ```html block with interactive UI
  const htmlMatch = content.match(/```html\s*\n([\s\S]+?)\n```/i);
  if (htmlMatch && htmlMatch[1]?.trim() && htmlMatch[1].length > 40) {
    const titleMatch =
      htmlMatch[1].match(/<title>(.*?)<\/title>/i) || htmlMatch[1].match(/<h[12]>(.*?)<\/h[12]>/i);
    return {
      id: makeId('html'),
      type: 'html',
      title: titleMatch?.[1]?.trim() || translate('crm.artifacts.html_widget_title'),
      subtitle: 'HTML / CSS / JS Sandbox',
      content: htmlMatch[1].trim(),
    };
  }

  // 3. Check for structured long documents (Weekly Plan, Project Brief, Meeting Summary, Strategic Plan)
  const trimmed = content.trim();
  const docHeadingMatch = trimmed.match(/^#\s+(.+)$/m);
  if (docHeadingMatch && docHeadingMatch[1] && trimmed.length >= 180) {
    const hasStructure = /##\s+|\n\d+\.\s+|\n-\s+\[[ x]\]|\n-\s+/.test(trimmed);
    if (hasStructure) {
      const rawTitle = docHeadingMatch[1].trim().replace(/[*_`#]/g, '');
      let subtitle = translate('crm.artifacts.markdown_doc');
      if (/план|plan/i.test(rawTitle)) {
        subtitle = translate('crm.artifacts.action_plan');
      } else if (/бриф|brief/i.test(rawTitle)) {
        subtitle = translate('crm.artifacts.project_brief');
      } else if (/сводка|итог|summary/i.test(rawTitle)) {
        subtitle = translate('crm.artifacts.meeting_summary');
      } else if (/отч[её]т|анализ|report|analysis|аудит|audit/i.test(rawTitle)) {
        subtitle = translate('crm.artifacts.analytics_report');
      }

      // Check for markdown checklist items
      const todoLines = trimmed.match(/^-\s*\[([ xX])\]\s*(.+)$/gm);
      const todoItems = todoLines
        ? todoLines.map((line, idx) => {
            const isDone = /^-\s*\[[xX]\]/.test(line);
            const label = line.replace(/^-\s*\[[ xX]\]\s*/, '').trim();
            return {
              id: `todo-item-${idx + 1}`,
              label,
              done: isDone,
            };
          })
        : undefined;

      return {
        id: makeId('doc'),
        type: 'document',
        title: rawTitle || translate('crm.artifacts.document_fallback'),
        subtitle,
        content: trimmed,
        ...(todoItems && todoItems.length > 0 ? { todoItems } : {}),
      };
    }
  }

  return null;
}
